import React from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import CachedResponse from '../common/CachedResponse';
import EntityCRUD from '../common/EntityCRUD';
import NotFound from '../NotFound';
import AppChoices from './AppChoices';
import { adminEntities } from './constants';
import LotSets from './LotSets';
import SalesLocation from './SalesLocation';
import Manufacturer from './Manufacturer';
import SectionsVideo from './SectionsVideo';
import Users from './Users';
import DocHubFields from './DocHubFields';
import DocHubVariables from './DocHubVariables';
import CourseTrainingIndex from './courseTraining/CourseTrainingIndex';
import useRefetchChoices from '../../hooks/useRefetchChoices';
import { isAdmin } from '../../helpers/roleHelpers';
import CommissionSheetOptions from './CommissionSheetOptions';
import CustomEntities from './CustomEntities';
import ConversationBankAdmin from './ConversationBankAdmin';
import ConversationBankEditor from './ConversationBankEditor';

const ManufacturerRoute = ({ triggerRefetch, handleRefetchComplete, refetchChoices }) => (
  <CachedResponse
    listEndpoint="app-choices"
    triggerRefetch={triggerRefetch}
    onRefetchComplete={handleRefetchComplete}
    render={() => <Manufacturer refetchChoices={refetchChoices} />}
  />
);

const AdminEntityRoute = ({ entity, printGroupGroupValues }) => (
  <EntityCRUD
    entityAppChoiceKey={entity.appChoiceKey}
    entityName={entity.name}
    endpoint={entity.endpoint}
    entityTableHeaders={
      typeof entity.tableHeaders === 'function' ? entity.tableHeaders(printGroupGroupValues) : entity?.tableHeaders
    }
    entityTableSearchKeys={entity.searchKeys}
    addFormRows={typeof entity.addRows === 'function' ? entity?.addRows(printGroupGroupValues) : entity.addRows}
    updateFormRows={
      typeof entity.updateRows === 'function' ? entity?.updateRows(printGroupGroupValues) : entity.updateRows
    }
    groupBy={entity?.groupBy}
    groupValues={typeof entity.groupValues === 'function' ? entity?.groupValues(printGroupGroupValues) : entity?.groupValues}
    showAdditionalHeader={entity?.showAdditionalHeader}
    draggable={entity?.draggable}
    onDragEndpoint={entity?.onDragEndpoint}
  />
);

const Admin = () => {
  const { triggerRefetch, refetchChoices, handleRefetchComplete } = useRefetchChoices();
  const adminUser = isAdmin();

  const printGroupGroupValues = JSON.parse(localStorage.getItem('app-choices'))?.find(
    v => v.key === 'Print Groups Categories'
  )?.values;

  const lenderEntity = adminEntities.find(e => e.appChoiceKey === 'lender');

  // Non-admins may only view Manufacturers and Lenders (read-only mutations gated in the pages).
  if (!adminUser) {
    return (
      <Switch>
        <Route exact path="/admin/manufacturer">
          <ManufacturerRoute
            triggerRefetch={triggerRefetch}
            handleRefetchComplete={handleRefetchComplete}
            refetchChoices={refetchChoices}
          />
        </Route>
        {lenderEntity && (
          <Route exact path={`/admin${lenderEntity.route}`}>
            <AdminEntityRoute entity={lenderEntity} printGroupGroupValues={printGroupGroupValues} />
          </Route>
        )}
        <Route path="*">
          <NotFound text="You are not authorized to view this page" />
        </Route>
      </Switch>
    );
  }

  return (
    <Switch>
      <Route exact path={'/admin/users'}>
        <CachedResponse listEndpoint={'app-choices'} render={appChoices => <Users appChoices={appChoices} />} />
      </Route>
      <Route exact path={'/admin/app-choices'}>
        <AppChoices />
      </Route>
      <Route exact path={'/admin/commission-sheet-options'}>
        <CommissionSheetOptions />
      </Route>
      <Route exact path={`/admin/sales-location`}>
        <SalesLocation />
      </Route>
      <Route exact path={`/admin/manufacturer`}>
        <ManufacturerRoute
          triggerRefetch={triggerRefetch}
          handleRefetchComplete={handleRefetchComplete}
          refetchChoices={refetchChoices}
        />
      </Route>
      {adminEntities.map(e => (
        <Route key={e.route} exact path={`/admin${e.route}`}>
          <AdminEntityRoute entity={e} printGroupGroupValues={printGroupGroupValues} />
        </Route>
      ))}
      <Route exact path={'/admin/lot-sets'}>
        <CachedResponse
          listEndpoint={'app-choices'}
          render={appChoices => <LotSets salesLocations={appChoices.find(c => c.key === 'salesLocation').values} />}
        />
      </Route>
      <Route exact path={'/admin/section-video'}>
        <SectionsVideo />
      </Route>
      <Route exact path={'/admin/dochub-fields'}>
        <DocHubFields />
      </Route>
      <Route exact path={'/admin/custom-entities'}>
        <CustomEntities />
      </Route>
      <Route exact path={'/admin/conversation-bank'}>
        <ConversationBankAdmin />
      </Route>
      <Route exact path={'/admin/conversation-bank/new'}>
        <ConversationBankEditor />
      </Route>
      <Route exact path={'/admin/conversation-bank/:id'}>
        <ConversationBankEditor />
      </Route>
      <Route exact path={'/admin/dochub-variables'}>
        <DocHubVariables />
      </Route>
      <Route
        path={'/admin/manage-mhc-sales-development-system/:courseId?'}
        render={({ match, location }) => (
          <Redirect
            to={{
              pathname: match.params.courseId
                ? `/admin/manage-sales-academy/${match.params.courseId}`
                : '/admin/manage-sales-academy',
              search: location.search,
              hash: location.hash
            }}
          />
        )}
      />
      <Route exact path={'/admin/manage-sales-academy/:courseId'}>
        <CourseTrainingIndex />
      </Route>
      <Route exact path={'/admin/manage-sales-academy'}>
        <CourseTrainingIndex />
      </Route>
    </Switch>
  );
};

export default Admin;
